<script setup lang="ts">
import { maskedLastFour } from '@privacy-bee/format/mask';
import { computed, nextTick, ref, watch } from 'vue';
import CardBrandIcon from './CardBrandIcon.vue';

// The multi-card manage panel (W-14/W-19).
//
// PURE and presentational: it takes the method list and the mode as props and
// emits intent events — it never calls the API or Stripe. The container owns the
// round trip; it fetches GET /payment-methods, handles these events against the
// collection API, and renders the Stripe add-card form (D-8/D-12).

export interface PaymentMethod {
    id: string;
    type: 'card' | 'link';
    brand?: string | null;
    last_four?: string | null;
    exp_month?: number | null;
    exp_year?: number | null;
    name?: string | null;
    link_email?: string | null;
    is_primary: boolean;
}

const props = withDefaults(
    defineProps<{
        methods: PaymentMethod[];
        /** 'settings' shows manage actions; 'checkout' adds per-purchase selection. */
        mode?: 'settings' | 'checkout';
        /** checkout only: the method chosen for THIS purchase (defaults to the primary). */
        selectedId?: string | null;
        /** disable Add when the saved-method cap is reached (D-23). */
        atCap?: boolean;
        /** disable actions while a request is in flight — including the checkout
         *  radios, so a selection cannot change under an order that is already
         *  submitting with the id it captured. */
        busy?: boolean;
        /** embedded in a container that owns its own chrome (e.g. a modal): always show the
         *  list, hide the collapsed summary + Manage/Done toggle. */
        embedded?: boolean;
        /** hide the Add button (e.g. while the container's own add/edit form is open). */
        hideAdd?: boolean;
        /** hide the Edit action on a surface that has no edit form to open (D-6 lives in
         *  the settings wallet; the business order page manages cards but does not edit them). */
        hideEdit?: boolean;
    }>(),
    { mode: 'settings', selectedId: null, atCap: false, busy: false, embedded: false, hideAdd: false, hideEdit: false },
);

const emit = defineEmits<{
    'set-primary': [id: string];
    edit: [id: string];
    delete: [id: string];
    add: [];
    /** checkout per-purchase selection (D-9): use for this order; never changes the saved primary. */
    select: [id: string];
}>();

const expanded = ref(false);
const openMenuId = ref<string | null>(null);
const confirmingDeleteId = ref<string | null>(null);
// Spoken by the always-mounted status region: the destructive question, then its outcome. The
// removal has no other voice — the row simply vanishes.
const announcement = ref('');
// The panel emits `delete` and the container performs it, so success is not ours to claim: on a
// failed request the row stays and the container reports the error. Announce the outcome only
// once the row is actually gone.
const pendingRemoval = ref<{ id: string; label: string } | null>(null);

watch(
    () => props.methods,
    (methods) => {
        const pending = pendingRemoval.value;
        if (!pending || methods.some((m) => m.id === pending.id)) return;

        announcement.value = `${pending.label} removed.`;
        pendingRemoval.value = null;
    },
    { deep: true },
);

// The list is shown when embedded (modal owns the chrome) or when the user expanded it.
const showList = computed(() => props.embedded || expanded.value);

const primary = computed<PaymentMethod | null>(() => props.methods.find((m) => m.is_primary) ?? null);

const selectedForPurchase = computed<PaymentMethod | null>(
    () => props.methods.find((m) => m.id === props.selectedId) ?? primary.value,
);

// In checkout, the collapsed summary reflects what will be charged for this order
// (the per-purchase selection); in settings it always reflects the saved primary.
const summaryMethod = computed<PaymentMethod | null>(() =>
    props.mode === 'checkout' ? selectedForPurchase.value : primary.value,
);

const summaryIsPerPurchase = computed(
    () => props.mode === 'checkout' && !!summaryMethod.value && summaryMethod.value.id !== primary.value?.id,
);

function isExpired(m: PaymentMethod): boolean {
    if (m.type !== 'card' || !m.exp_year || !m.exp_month) return false;
    // End of the expiry month.
    const end = new Date(m.exp_year, m.exp_month, 0, 23, 59, 59);
    return end.getTime() < Date.now();
}

function brandTitle(brand?: string | null): string {
    return brand ? `${brand.charAt(0).toUpperCase()}${brand.slice(1)}` : 'Card';
}

// The primary visual line: the masked number for cards, the email for Link.
function numberLabel(m: PaymentMethod): string {
    if (m.type === 'link') return m.link_email ? `Link · ${m.link_email}` : 'Link';
    return m.last_four ? maskedLastFour(m.last_four) : brandTitle(m.brand);
}

// Accessible / textual label (used for aria + the per-purchase note).
function label(m: PaymentMethod): string {
    if (m.type === 'link') return m.link_email ? `Link (${m.link_email})` : 'Link';
    return m.last_four ? `${brandTitle(m.brand)} ending ${m.last_four}` : brandTitle(m.brand);
}

// The radio's <label> wrapper holds only the input — the card identity and its Primary/Expired
// badges are siblings that a <label> cannot reach — so the whole name is built here.
function selectLabel(m: PaymentMethod): string {
    const state = [m.is_primary ? 'primary' : '', isExpired(m) ? 'expired' : ''].filter(Boolean);
    return state.length ? `${label(m)} (${state.join(', ')})` : label(m);
}

function expiryLabel(m: PaymentMethod): string {
    if (m.type !== 'card' || !m.exp_month || !m.exp_year) return '';
    return `Exp ${String(m.exp_month).padStart(2, '0')}/${String(m.exp_year).slice(-2)}`;
}

function toggleManage(): void {
    expanded.value = !expanded.value;
    openMenuId.value = null;
    confirmingDeleteId.value = null;
    // Cleared so re-asking about the same card is a text change the region will speak again.
    announcement.value = '';
}

function toggleMenu(id: string): void {
    openMenuId.value = openMenuId.value === id ? null : id;
}

// Focus follows the control that replaces the one being unmounted. Without this the kebab item or
// confirm button the user just activated is destroyed under their focus, dropping them to <body>.
const focusTargets = new Map<string, HTMLElement>();

function setFocusTarget(key: string, el: unknown): void {
    if (el instanceof HTMLElement) focusTargets.set(key, el);
    else focusTargets.delete(key);
}

function focusAfterRender(key: string): void {
    void nextTick().then(() => focusTargets.get(key)?.focus());
}

function closeMenu(id: string): void {
    if (openMenuId.value !== id) return;
    openMenuId.value = null;
    focusAfterRender(`kebab-${id}`);
}

// The menu is a sibling of its kebab inside the row, so leaving the row is what closes it —
// watching the menu alone would close it on the click that is on its way to the kebab.
function onRowFocusOut(event: FocusEvent, id: string): void {
    if (openMenuId.value !== id) return;
    const next = event.relatedTarget;
    const row = event.currentTarget;
    if (row instanceof HTMLElement && next instanceof Node && row.contains(next)) return;
    openMenuId.value = null;
}

function onSetPrimary(id: string): void {
    openMenuId.value = null;
    emit('set-primary', id);
    // The menu holding the activated button is gone; the kebab that opened it is not, and is
    // the one control in the row that is never disabled while the parent is busy.
    focusAfterRender(`kebab-${id}`);
}

// No focusAfterRender here: the container answers `edit` by revealing its own editor and moves
// focus into it. A move from this side would win the same tick and pull focus back out.
function onEdit(id: string): void {
    openMenuId.value = null;
    emit('edit', id);
}

// Delete is a two-step inline confirm (no second modal): the kebab Delete opens an in-row
// confirm, and only the confirm button emits the delete.
function onDelete(id: string): void {
    const m = props.methods.find((x) => x.id === id);
    openMenuId.value = null;
    confirmingDeleteId.value = id;
    announcement.value = '';
    // Cancel, not Remove: the safe option carries the destructive question as its description,
    // which is why the live region stays empty here and speaks only the outcome.
    focusAfterRender(`cancel-${id}`);
}

function confirmDelete(id: string): void {
    const m = props.methods.find((x) => x.id === id);
    confirmingDeleteId.value = null;
    pendingRemoval.value = m ? { id, label: label(m) } : null;
    emit('delete', id);
    announcement.value = m ? `Removing ${label(m)}…` : 'Removing payment method…';
    // The row is on its way out and Add is both disabled the moment the container goes busy and
    // absent at the cap, so land on the panel itself — the node that outlives every row.
    focusAfterRender('panel');
}

function cancelDelete(id: string): void {
    confirmingDeleteId.value = null;
    pendingRemoval.value = null;
    announcement.value = '';
    focusAfterRender(`kebab-${id}`);
}

function onSelect(id: string): void {
    // Per-purchase only (D-9): emit the choice and collapse. The saved primary
    // is unchanged — the parent updates `selectedId`, not the primary.
    emit('select', id);
    expanded.value = false;
}
</script>

<template>
    <div
        :ref="(el: unknown) => setFocusTarget('panel', el)"
        class="payment-methods-panel"
        tabindex="-1"
        data-focus-ring="none"
    >
        <!-- Mounted before any text lands (a region that appears already holding its text is not
             reliably announced) and visually hidden, so it adds nothing to the layout. -->
        <p class="pm-sr-only" role="status">{{ announcement }}</p>

        <!-- Collapsed summary (hidden when embedded — the container owns the chrome) -->
        <div v-if="!embedded && !showList" class="pm-summary" data-testid="pm-summary">
            <template v-if="summaryMethod">
                <CardBrandIcon :brand="summaryMethod.brand" :type="summaryMethod.type" />
                <span class="pm-main">
                    <span class="pm-summary-label" data-testid="pm-summary-label">{{ numberLabel(summaryMethod) }}</span>
                    <span v-if="summaryMethod.name" class="pm-sub">{{ summaryMethod.name }}</span>
                </span>
                <span v-if="isExpired(summaryMethod)" class="pm-badge-expired" data-testid="pm-summary-expired">Expired</span>
                <span v-if="summaryIsPerPurchase" class="pm-per-purchase" data-testid="pm-summary-per-purchase">
                    Used for this purchase · Primary: {{ primary ? numberLabel(primary) : '—' }}
                </span>
            </template>
            <span v-else class="pm-empty" data-testid="pm-empty">No payment method on file</span>

            <button
                type="button"
                class="pm-manage"
                :aria-expanded="showList"
                aria-label="Manage payment methods"
                data-testid="pm-manage"
                @click="toggleManage"
            >
                Manage
            </button>
        </div>

        <!-- Expanded / embedded list -->
        <div v-else class="pm-list" data-testid="pm-list">
            <button
                v-if="!embedded"
                type="button"
                class="pm-manage pm-done"
                :aria-expanded="showList"
                data-testid="pm-manage"
                @click="toggleManage"
            >
                Done
            </button>

            <ul
                class="pm-rows"
                :role="mode === 'checkout' ? 'group' : undefined"
                :aria-label="mode === 'checkout' ? 'Card to charge for this purchase' : undefined"
            >
                <li
                    v-for="m in methods"
                    :key="m.id"
                    class="pm-row"
                    :class="{ 'pm-row-selected': mode === 'checkout' && selectedForPurchase?.id === m.id }"
                    :data-testid="`pm-row-${m.id}`"
                    @focusout="onRowFocusOut($event, m.id)"
                    @keydown.esc="closeMenu(m.id)"
                >
                    <!-- In-row delete confirmation (replaces the row content; no second modal). -->
                    <template v-if="confirmingDeleteId === m.id">
                        <span :id="`pm-confirm-${m.id}`" class="pm-confirm-text">Remove {{ numberLabel(m) }}?</span>
                        <div class="pm-confirm-actions">
                            <button
                                :ref="(el: unknown) => setFocusTarget(`cancel-${m.id}`, el)"
                                type="button"
                                class="pm-confirm-cancel"
                                :aria-describedby="`pm-confirm-${m.id}`"
                                :data-testid="`pm-delete-cancel-${m.id}`"
                                @click="cancelDelete(m.id)"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                class="pm-confirm-remove"
                                :disabled="busy"
                                :aria-describedby="`pm-confirm-${m.id}`"
                                :data-testid="`pm-delete-confirm-${m.id}`"
                                @click="confirmDelete(m.id)"
                            >
                                Remove
                            </button>
                        </div>
                    </template>

                    <template v-else>
                    <label v-if="mode === 'checkout'" class="pm-select">
                        <input
                            type="radio"
                            name="pm-select-for-purchase"
                            :value="m.id"
                            :checked="selectedForPurchase?.id === m.id"
                            :disabled="isExpired(m) || busy"
                            :aria-label="selectLabel(m)"
                            :data-testid="`pm-select-${m.id}`"
                            @change="onSelect(m.id)"
                        />
                    </label>

                    <CardBrandIcon :brand="m.brand" :type="m.type" />

                    <span class="pm-main">
                        <span class="pm-row-label">
                            {{ numberLabel(m) }}
                            <span v-if="m.is_primary" class="pm-badge-primary" :data-testid="`pm-primary-${m.id}`">Primary</span>
                            <span v-if="isExpired(m)" class="pm-badge-expired" :data-testid="`pm-expired-${m.id}`">Expired</span>
                        </span>
                        <span v-if="m.name || expiryLabel(m)" class="pm-sub">
                            <template v-if="m.name">{{ m.name }}</template>
                            <template v-if="m.name && expiryLabel(m)"> · </template>
                            <template v-if="expiryLabel(m)">{{ expiryLabel(m) }}</template>
                        </span>
                    </span>

                    <button
                        :ref="(el: unknown) => setFocusTarget(`kebab-${m.id}`, el)"
                        type="button"
                        class="pm-kebab"
                        :aria-label="`Options for ${label(m)}`"
                        :aria-expanded="openMenuId === m.id"
                        :data-testid="`pm-kebab-${m.id}`"
                        @click="toggleMenu(m.id)"
                    >
                        ⋯
                    </button>

                    <!-- Three plain buttons in a popup. No role="menu": that role obliges arrow-key
                         roving focus, type-ahead and Home/End, none of which this needs. -->
                    <div v-if="openMenuId === m.id" class="pm-menu" :data-testid="`pm-menu-${m.id}`">
                        <button
                            v-if="!m.is_primary"
                            type="button"
                            :disabled="busy"
                            :data-testid="`pm-setprimary-${m.id}`"
                            @click="onSetPrimary(m.id)"
                        >
                            Set as primary
                        </button>
                        <button
                            v-if="m.type !== 'link' && !hideEdit"
                            type="button"
                            :disabled="busy"
                            :data-testid="`pm-edit-${m.id}`"
                            @click="onEdit(m.id)"
                        >
                            Edit
                        </button>
                        <button
                            type="button"
                            class="pm-menu-danger"
                            :disabled="busy"
                            :data-testid="`pm-delete-${m.id}`"
                            @click="onDelete(m.id)"
                        >
                            Delete
                        </button>
                    </div>
                    </template>
                </li>
            </ul>

            <button
                v-if="!hideAdd"
                :ref="(el: unknown) => setFocusTarget('add', el)"
                type="button"
                class="pm-add"
                :disabled="atCap || busy"
                data-testid="pm-add"
                @click="emit('add')"
            >
                <span class="pm-add-plus" aria-hidden="true">+</span> Add payment method
            </button>
            <p v-if="atCap && !hideAdd" class="pm-cap-note" data-testid="pm-cap-note">
                You've reached the limit of saved payment methods. Remove one to add another.
            </p>
        </div>
    </div>
</template>

<!-- Vanilla CSS, no Tailwind or SCSS directives: the panel renders the same in every app that
     mounts it without depending on that app's build pipeline. -->
<style scoped>
/* A focus target for script alone (tabindex -1, out of the Tab order), not a control, so it takes no
   ring: it stands aside from the design system's focus floor, and the browser's own ring goes with it. */
.payment-methods-panel {
    outline: none;
    width: 100%;
    font-size: 14px;
    color: #18181b;
}

/* Visually-hidden live region (spelled out rather than using a utility class, for the same
   reason the rest of this block is vanilla CSS). */
.pm-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
}

/* Shared two-line text block (masked number + cardholder/expiry) */
.pm-main {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    flex: 1 1 auto;
}
.pm-summary-label,
.pm-row-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    letter-spacing: 0.01em;
}
.pm-sub {
    font-size: 12px;
    color: #71717a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pm-empty {
    color: #71717a;
    flex: 1 1 auto;
}
.pm-per-purchase {
    flex-basis: 100%;
    font-size: 12px;
    color: #2563eb;
}

/* Collapsed summary */
.pm-summary {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

/* Manage / Done toggle */
.pm-manage {
    border: 1px solid #d4d4d8;
    background: #fff;
    color: #3f3f46;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}
.pm-manage:hover {
    background: #fafafa;
}
.pm-summary .pm-manage {
    margin-left: auto;
}
.pm-list > .pm-done {
    align-self: flex-end;
}

/* Expanded list */
.pm-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.pm-rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.pm-row {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #e4e4e7;
    border-radius: 10px;
    background: #fff;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.pm-row-selected {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
}
.pm-select {
    display: inline-flex;
    align-items: center;
}
.pm-select input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    margin: 0;
    accent-color: #2563eb;
}

/* Badges */
.pm-badge-primary,
.pm-badge-expired {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 9999px;
    line-height: 1.5;
    white-space: nowrap;
    letter-spacing: 0;
}
.pm-badge-primary {
    background: #eff6ff;
    color: #2563eb;
}
.pm-badge-expired {
    background: #fef2f2;
    color: #dc2626;
}

/* Kebab + menu */
.pm-kebab {
    margin-left: auto;
    border: none;
    background: transparent;
    font-size: 20px;
    line-height: 1;
    color: #52525b;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 6px;
    align-self: center;
}
.pm-kebab:hover {
    background: #f4f4f5;
}
.pm-menu {
    position: absolute;
    top: calc(100% - 4px);
    right: 10px;
    z-index: 10;
    min-width: 168px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #e4e4e7;
    border-radius: 10px;
    box-shadow: 0 10px 24px rgba(9, 9, 11, 0.12);
    overflow: hidden;
}
.pm-menu button {
    text-align: left;
    border: none;
    background: #fff;
    padding: 10px 14px;
    font-size: 13px;
    color: #3f3f46;
    cursor: pointer;
}
.pm-menu button:hover {
    background: #f4f4f5;
}
.pm-menu button:disabled {
    color: #a1a1aa;
    cursor: not-allowed;
}
.pm-menu-danger {
    color: #dc2626 !important;
}

/* In-row delete confirm */
.pm-confirm-text {
    flex: 1 1 auto;
    font-size: 13px;
    color: #3f3f46;
}
.pm-confirm-actions {
    display: flex;
    gap: 8px;
    margin-left: auto;
}
.pm-confirm-cancel,
.pm-confirm-remove {
    border-radius: 6px;
    padding: 5px 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
}
.pm-confirm-cancel {
    background: #fff;
    border-color: #d4d4d8;
    color: #3f3f46;
}
.pm-confirm-cancel:hover {
    background: #fafafa;
}
.pm-confirm-remove {
    background: #dc2626;
    color: #fff;
}
.pm-confirm-remove:hover:not(:disabled) {
    background: #b91c1c;
}
.pm-confirm-remove:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Add */
.pm-add {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #d4d4d8;
    background: #fff;
    color: #2563eb;
    border-radius: 6px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.pm-add:hover:not(:disabled) {
    background: #fafafa;
}
.pm-add:disabled {
    color: #a1a1aa;
    cursor: not-allowed;
}
.pm-add-plus {
    font-size: 16px;
    line-height: 1;
}
.pm-cap-note {
    margin: 0;
    font-size: 12px;
    color: #a1a1aa;
}
</style>
